import { STOCK_DECIMALS, parseStockAmount } from '@/shared/lib/stock-quantity'

/**
 * The counting pad for articles with "Teilmengen zählen" (CHG-2026-09-stock-usage-and-price-twins,
 * D9, task 11.4), kept out of the component so every key can be tested directly.
 *
 * The entry is the text the counter sees, in de-AT form ("1,85"): digits, at most one comma, at
 * most 3 decimals. Whole-number articles never reach this module — their pad is unchanged.
 */

export type FractionalKey =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | ','
  | 'backspace'
  | 'clear'
  /** ¼, ½, ¾: the fraction of the whole number typed so far ("2" → "2,5"). */
  | 'quarter'
  | 'half'
  | 'threeQuarters'

/** Integer digits the pad accepts, the same bound as the whole-number pad. */
export const MAX_INTEGER_DIGITS = 5

const QUICK_FRACTIONS: Record<'quarter' | 'half' | 'threeQuarters', string> = {
  quarter: '25',
  half: '5',
  threeQuarters: '75',
}

/** The entry after one key press. */
export function pressFractionalKey(
  current: string,
  key: FractionalKey,
): string {
  if (key === 'clear') return ''
  if (key === 'backspace') return current.slice(0, -1)
  const [integerPart, fraction] = current.split(',') as [
    string,
    string | undefined,
  ]
  if (key === 'quarter' || key === 'half' || key === 'threeQuarters') {
    return `${integerPart === '' ? '0' : integerPart},${QUICK_FRACTIONS[key]}`
  }
  if (key === ',') {
    if (fraction !== undefined) return current
    return current === '' ? '0,' : `${current},`
  }
  // A digit.
  if (fraction !== undefined) {
    return fraction.length >= STOCK_DECIMALS ? current : current + key
  }
  if (integerPart.length >= MAX_INTEGER_DIGITS) return current
  // A leading zero carries no meaning and would make "0" unreadable as "not present".
  if (current === '0') return key
  return current + key
}

/**
 * The entry as a quantity, or null while it is empty or not a number. A trailing comma ("2,")
 * reads as the whole number typed so far. The value is the typed one, never float arithmetic.
 */
export function fractionalEntryValue(entry: string): number | null {
  const trimmed = entry.endsWith(',') ? entry.slice(0, -1) : entry
  if (trimmed === '') return null
  const parsed = parseStockAmount(trimmed)
  return parsed.ok ? parsed.value : null
}
